
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Sparkles, RefreshCw } from "lucide-react";
import { AIJobInputForm } from "./AIJobInputForm";
import { JobGenerationPreview } from "./JobGenerationPreview";
import { JobScoringPanel } from "./JobScoringPanel";
import { useAIJobGeneration } from "./hooks/useAIJobGeneration";
import { aiJobInputSchema, type AIJobInputValues } from "./schemas/aiJobInputSchema";

interface AIJobCreationTabProps {
  clientId?: string;
  clientName?: string;
  onSuccess?: () => void;
  onClose: () => void;
}

export function AIJobCreationTab({ 
  clientId, 
  clientName, 
  onSuccess, 
  onClose 
}: AIJobCreationTabProps) {
  const [generatedJob, setGeneratedJob] = useState<any>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const form = useForm<AIJobInputValues>({
    resolver: zodResolver(aiJobInputSchema),
    defaultValues: {
      company: clientName || "",
    },
  });

  const { 
    generateJobPackage, 
    acceptJobPackage, 
    refineJobPackage,
    isGenerating, 
    isAccepting,
    isRefining 
  } = useAIJobGeneration({ 
    clientId, 
    onSuccess, 
    onClose 
  });

  const handleGenerate = async (data: AIJobInputValues) => {
    const result = await generateJobPackage(data);
    if (result) {
      setGeneratedJob(result);
      setIsPreviewMode(true);
    }
  };

  const handleRefine = async (feedback: string) => {
    if (!generatedJob) return;
    const refined = await refineJobPackage(generatedJob, feedback);
    if (refined) {
      setGeneratedJob(refined);
    }
  };

  const handleAccept = () => {
    if (generatedJob) {
      acceptJobPackage(generatedJob);
    }
  };

  const handleBackToInput = () => {
    setIsPreviewMode(false);
  };

  if (isPreviewMode && generatedJob) {
    return (
      <div className="space-y-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <JobGenerationPreview 
              generatedJob={generatedJob}
              onRefine={handleRefine}
              isRefining={isRefining}
            />
          </div>
          <div className="lg:col-span-1">
            <JobScoringPanel jobData={generatedJob} />
          </div>
        </div>
        
        <div className="flex justify-between pt-4 border-t sticky bottom-0 bg-background">
          <Button variant="outline" onClick={handleBackToInput}>
            Back to Input
          </Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleRefine("Please improve the overall quality and appeal")}
              disabled={isRefining}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {isRefining ? "Refining..." : "Auto-Refine"}
            </Button>
            <Button onClick={handleAccept} disabled={isAccepting}>
              {isAccepting ? "Creating Job..." : "Accept & Create Job"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleGenerate)} className="space-y-6">
          <AIJobInputForm form={form} clientName={clientName} />
          
          <div className="flex justify-end pt-4 border-t sticky bottom-0 bg-background">
            <Button 
              type="submit" 
              disabled={isGenerating} 
              className="min-w-[160px]"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {isGenerating ? "Generating..." : "Generate Job Package"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
